import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
  RiArrowLeftLine,
  RiAddLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiCloseLine,
  RiErrorWarningLine,
  RiSaveLine,
  RiInboxLine,
  RiFileTextLine,
  RiBuilding2Line,
  RiBankLine,
  RiUserLine,
  RiUserAddLine,
  RiMapPinLine,
  RiPhoneLine,
  RiMailLine,
  RiHashtag,
} from "react-icons/ri";
import { FaCodeBranch } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { LiaRupeeSignSolid } from "react-icons/lia";

const BASE_URL = process.env.REACT_APP_API_URL;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const calcGST = (subTotal, cgstRate = 2.5, sgstRate = 2.5) => {
  const s = Number(subTotal) || 0;
  const cgst = Math.round(((s * cgstRate) / 100) * 100) / 100;
  const sgst = Math.round(((s * sgstRate) / 100) * 100) / 100;
  const total = Math.round((s + cgst + sgst) * 100) / 100;
  return { cgst, sgst, total };
};

/* ─── Toast ─── */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 opacity-50 hover:opacity-100">
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ─── Sub-client creation modal (identical pattern to Splitbills) ─── */
const subClientFields = [
  {
    label: "Sub-client Name",
    name: "branchName",
    icon: FaCodeBranch,
    required: true,
  },
  { label: "Contact Person", name: "contactPerson", icon: RiUserLine },
  { label: "Phone", name: "phone", icon: RiPhoneLine, type: "tel" },
  { label: "Email", name: "email", icon: RiMailLine, type: "email" },
  { label: "Address", name: "address", icon: RiMapPinLine },
  { label: "PIN", name: "pin", icon: RiHashtag },
];

const SubClientModal = ({ bankId, clientName, onClose, onCreated, notify }) => {
  const [form, setForm] = useState({
    branchName: "",
    contactPerson: "",
    phone: "",
    email: "",
    address: "",
    pin: "",
  });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.branchName.trim()) {
      notify("Sub-client name is required", "error");
      return;
    }
    if (!bankId) {
      notify("No client ID found", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${BASE_URL}/branch/create-branch`, {
        ...form,
        bankId,
      });
      const created = res.data?.data || res.data;
      notify("Sub-client created successfully");
      onCreated(created);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to create sub-client",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white border border-indigo-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-md shadow-indigo-200">
              <FaCodeBranch className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-indigo-900">
                Add Sub-client
              </h2>
              {clientName && (
                <p className="text-[11px] text-indigo-400 font-medium">
                  Under:{" "}
                  <span className="text-violet-600 font-bold">
                    {clientName}
                  </span>
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-indigo-400 hover:text-indigo-700 transition rounded-lg p-1 hover:bg-indigo-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {subClientFields.map(
              ({ label, name, icon: Icon, type = "text", required }) => (
                <div
                  key={name}
                  className={name === "address" ? "col-span-2" : "col-span-1"}
                >
                  <label className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest mb-1.5 block">
                    {label}
                    {required && <span className="text-red-400 ml-0.5">*</span>}
                  </label>
                  <div className="relative">
                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-indigo-300 pointer-events-none" />
                    <input
                      type={type}
                      name={name}
                      value={form[name]}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          [e.target.name]: e.target.value,
                        }))
                      }
                      placeholder={label}
                      className="w-full bg-indigo-50/60 border border-indigo-200 rounded-xl pl-8 pr-3 py-2 text-xs text-slate-800 placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                    />
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-indigo-50 bg-slate-50/50">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
          >
            <RiCloseLine className="w-4 h-4" /> Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white transition shadow-md shadow-indigo-200 disabled:opacity-60"
          >
            {saving ? <Spinner /> : <RiCheckLine className="w-4 h-4" />}
            {saving ? "Creating…" : "Create Sub-client"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/* ─── Summary bar ─── */
const SummaryBar = ({
  grandSubTotal,
  allocatedSubTotal,
  branchCount,
  borrowerCount,
}) => {
  const remaining = grandSubTotal - allocatedSubTotal;
  const isOver = remaining < -0.005;
  const isBalanced = Math.abs(remaining) < 0.005;

  return (
    <div className="bg-white border border-indigo-100 rounded-2xl shadow-sm px-6 py-4 flex flex-wrap items-center gap-6">
      <div className="flex flex-col">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          Bill Sub Total
        </span>
        <span className="text-lg font-extrabold text-slate-800 font-mono">
          ₹{fmt(grandSubTotal)}
        </span>
      </div>
      <div className="h-8 w-px bg-slate-100" />
      <div className="flex flex-col">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          Allocated ({branchCount} branch{branchCount !== 1 ? "es" : ""},{" "}
          {borrowerCount} borrower{borrowerCount !== 1 ? "s" : ""})
        </span>
        <span className="text-lg font-extrabold text-indigo-700 font-mono">
          ₹{fmt(allocatedSubTotal)}
        </span>
      </div>
      <div className="h-8 w-px bg-slate-100" />
      <div className="flex flex-col">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          Remaining
        </span>
        <span
          className={`text-lg font-extrabold font-mono ${isBalanced ? "text-emerald-600" : isOver ? "text-red-600" : "text-amber-600"}`}
        >
          ₹{fmt(Math.abs(remaining))}
          {isOver ? " (over)" : isBalanced ? " ✓" : ""}
        </span>
      </div>
      <div className="ml-auto">
        {isBalanced ? (
          <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg">
            <RiCheckLine className="w-3.5 h-3.5" /> Balanced
          </span>
        ) : isOver ? (
          <span className="flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg">
            <RiErrorWarningLine className="w-3.5 h-3.5" /> Over-allocated
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
            <RiErrorWarningLine className="w-3.5 h-3.5" /> Not balanced
          </span>
        )}
      </div>
    </div>
  );
};

const newBorrower = () => ({
  id: crypto.randomUUID(),
  borrowerName: "",
  subtotal: "",
  perAccount: "",
});
const newBranchGroup = () => ({
  id: crypto.randomUUID(),
  branchId: "",
  branchName: "",
  borrowers: [newBorrower()],
});

/* ─── Branch Group ─── */
const BranchGroup = ({
  group,
  groupIdx,
  branches,
  bankId,
  clientName,
  cgstRate,
  sgstRate,
  onUpdateGroup,
  onRemoveGroup,
  onAddBorrower,
  onUpdateBorrower,
  onRemoveBorrower,
  onBranchCreated,
  notify,
}) => {
  const [showModal, setShowModal] = useState(false);

  const groupSubTotal = group.borrowers.reduce(
    (s, b) => s + (parseFloat(b.subtotal) || 0),
    0,
  );
  const {
    cgst: groupCgst,
    sgst: groupSgst,
    total: groupTotal,
  } = calcGST(groupSubTotal, cgstRate, sgstRate);

  return (
    <>
      <div className="bg-white border border-indigo-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 bg-indigo-50/40 border-b border-indigo-100 flex-wrap">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <FaCodeBranch className="w-3.5 h-3.5 text-indigo-600" />
          </div>

          <select
            value={group.branchId}
            onChange={(e) => {
              const selected = branches.find((b) => b._id === e.target.value);
              onUpdateGroup(groupIdx, {
                branchId: e.target.value,
                branchName: selected?.branchName || "",
              });
            }}
            className="flex-1 min-w-[180px] px-3 py-1.5 bg-white border border-indigo-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
          >
            <option value="">— Select Sub-client —</option>
            {branches.map((b) => (
              <option key={b._id} value={b._id}>
                {b.branchName}
              </option>
            ))}
          </select>

          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-200 text-violet-700 rounded-xl text-xs font-bold hover:bg-violet-100 transition flex-shrink-0"
          >
            <FaCodeBranch className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Add Sub-client</span>
          </button>

          <div className="hidden sm:flex items-center gap-3 bg-white border border-indigo-100 rounded-xl px-4 py-1.5 text-xs font-mono flex-shrink-0">
            <span className="text-slate-400">Sub</span>
            <span className="font-bold text-indigo-700">
              ₹{fmt(groupSubTotal)}
            </span>
            <span className="text-slate-300">|</span>
            <span className="text-slate-400">Total</span>
            <span className="font-bold text-indigo-800">
              ₹{fmt(groupTotal)}
            </span>
          </div>

          <button
            onClick={() => onAddBorrower(groupIdx)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-xl text-xs font-bold hover:bg-indigo-100 transition flex-shrink-0"
          >
            <RiUserAddLine className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Add Borrower</span>
          </button>

          <button
            onClick={() => onRemoveGroup(groupIdx)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-red-100 text-red-300 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition flex-shrink-0"
            title="Remove branch"
          >
            <RiDeleteBinLine className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-indigo-50 bg-slate-50/60">
                {[
                  "#",
                  "Borrower's Name",
                  "Per A/C (₹)",
                  "Sub Total (₹)",
                  "CGST",
                  "SGST",
                  "Total (₹)",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.borrowers.map((b, bIdx) => {
                const sub = parseFloat(b.subtotal) || 0;
                const { cgst, sgst, total } = calcGST(sub, cgstRate, sgstRate);
                return (
                  <tr
                    key={b.id}
                    className="border-b border-slate-50 hover:bg-indigo-50/10 transition"
                  >
                    <td className="px-4 py-2.5 text-xs text-slate-400 font-mono">
                      {bIdx + 1}
                    </td>
                    <td className="px-3 py-2">
                      <div className="relative">
                        <RiUserLine className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-indigo-300 pointer-events-none" />
                        <input
                          type="text"
                          value={b.borrowerName}
                          onChange={(e) =>
                            onUpdateBorrower(groupIdx, bIdx, {
                              borrowerName: e.target.value,
                            })
                          }
                          placeholder="Borrower's name"
                          className="w-full min-w-[160px] pl-8 pr-3 py-2 bg-indigo-50/60 border border-indigo-200 rounded-xl text-xs text-slate-700 placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={b.perAccount}
                        onChange={(e) =>
                          onUpdateBorrower(groupIdx, bIdx, {
                            perAccount: e.target.value,
                          })
                        }
                        placeholder="0"
                        step="0.01"
                        min={0}
                        className="w-24 px-3 py-2 bg-indigo-50/60 border border-indigo-200 rounded-xl text-xs text-slate-700 font-mono placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={b.subtotal}
                        onChange={(e) =>
                          onUpdateBorrower(groupIdx, bIdx, {
                            subtotal: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        step="0.01"
                        min={0}
                        className="w-28 px-3 py-2 bg-indigo-50/60 border border-indigo-200 rounded-xl text-xs text-slate-700 font-mono placeholder-indigo-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-500 whitespace-nowrap">
                      ₹{fmt(cgst)}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-500 whitespace-nowrap">
                      ₹{fmt(sgst)}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="font-extrabold text-indigo-700 font-mono text-sm">
                        ₹{fmt(total)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => onRemoveBorrower(groupIdx, bIdx)}
                        disabled={group.borrowers.length === 1}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-red-100 text-red-300 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <RiCloseLine className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {group.borrowers.length > 1 && (
              <tfoot>
                <tr className="bg-indigo-50/50 border-t border-indigo-100">
                  <td
                    colSpan={2}
                    className="px-4 py-2.5 text-[10px] font-bold text-indigo-600 uppercase tracking-widest"
                  >
                    Branch Total ({group.borrowers.length} borrowers)
                  </td>
                  <td className="px-4 py-2.5 text-xs font-bold font-mono text-slate-700">
                    ₹{fmt(groupSubTotal)}
                  </td>
                  <td className="px-4 py-2.5 text-xs font-bold font-mono text-slate-700">
                    ₹{fmt(groupCgst)}
                  </td>
                  <td className="px-4 py-2.5 text-xs font-bold font-mono text-slate-700">
                    ₹{fmt(groupSgst)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="font-extrabold text-indigo-800 font-mono text-sm">
                      ₹{fmt(groupTotal)}
                    </span>
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {showModal && (
        <SubClientModal
          bankId={bankId}
          clientName={clientName}
          onClose={() => setShowModal(false)}
          onCreated={(newBranch) => {
            onBranchCreated(newBranch, groupIdx);
            setShowModal(false);
          }}
          notify={notify}
        />
      )}
    </>
  );
};

const CreateSizeWiseSplitBill = () => {
  const navigate = useNavigate();
  const { id: invoiceId } = useParams();
  const location = useLocation();
  const state = location.state || {};

  const {
    splitBillId,
    billNumber = "",
    invoiceNumber = "",
    grandSubTotal = 0,
    billTo,
    cgstRate: invCgst = 2.5,
    sgstRate: invSgst = 2.5,
  } = state;

  const bankId = state.releaseOrder?.client?._id || state.releaseOrder?.client;
  const clientName =
    billTo?.company || state.releaseOrder?.client?.bankName || "Client";

  // Resolved values — may come from route state or be fetched on mount
  const [resolvedSplitBillId, setResolvedSplitBillId] = useState(
    splitBillId || null,
  );
  const [resolvedGrandSubTotal, setResolvedGrandSubTotal] = useState(
    grandSubTotal || 0,
  );
  const [resolvedBillNumber, setResolvedBillNumber] = useState(
    billNumber || "",
  );
  const [fetchingBill, setFetchingBill] = useState(false);

  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [groups, setGroups] = useState([newBranchGroup()]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (message, type = "success") => setToast({ message, type });

  // If we landed here without a splitBillId (e.g. direct nav or missing state),
  // fetch the latest size-wise split bill for this invoice from the API.
  useEffect(() => {
    if (resolvedSplitBillId) return; // already have it
    if (!invoiceId) return;
    setFetchingBill(true);
    axios
      .get(`${BASE_URL}/size-wise-split/${invoiceId}/size-split-bills`)
      .then((res) => {
        const bills = res.data?.data?.splitBills || [];
        if (bills.length) {
          const latest = bills[bills.length - 1];
          setResolvedSplitBillId(latest._id);
          setResolvedGrandSubTotal(latest.grandSubTotal || 0);
          setResolvedBillNumber(latest.billNumber || "");
        }
      })
      .catch(() =>
        notify("Failed to load size-wise split bill details", "error"),
      )
      .finally(() => setFetchingBill(false));
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!bankId) return;
    setBranchLoading(true);
    axios
      .get(`${BASE_URL}/branch/get-branches-by-bank/${bankId}`)
      .then((res) => setBranches(res.data?.data || []))
      .catch(() => notify("Failed to load sub-clients", "error"))
      .finally(() => setBranchLoading(false));
  }, [bankId]);

  const handleBranchCreated = (newBranch, groupIdx) => {
    setBranches((prev) => {
      const exists = prev.find((b) => b._id === newBranch._id);
      return exists ? prev : [...prev, newBranch];
    });
    setGroups((g) =>
      g.map((grp, i) =>
        i === groupIdx
          ? {
              ...grp,
              branchId: newBranch._id,
              branchName: newBranch.branchName,
            }
          : grp,
      ),
    );
    notify(`Sub-client "${newBranch.branchName}" created & selected`);
  };

  const allocatedSubTotal = groups.reduce(
    (s, g) =>
      s + g.borrowers.reduce((ss, b) => ss + (parseFloat(b.subtotal) || 0), 0),
    0,
  );
  const totalBorrowers = groups.reduce((s, g) => s + g.borrowers.length, 0);
  const isBalanced = Math.abs(resolvedGrandSubTotal - allocatedSubTotal) < 0.01;

  const addGroup = () => setGroups((g) => [...g, newBranchGroup()]);
  const updateGroup = (gIdx, patch) =>
    setGroups((g) =>
      g.map((grp, i) => (i === gIdx ? { ...grp, ...patch } : grp)),
    );
  const removeGroup = (gIdx) =>
    setGroups((g) => g.filter((_, i) => i !== gIdx));
  const addBorrower = (gIdx) =>
    setGroups((g) =>
      g.map((grp, i) =>
        i === gIdx
          ? { ...grp, borrowers: [...grp.borrowers, newBorrower()] }
          : grp,
      ),
    );
  const updateBorrower = (gIdx, bIdx, patch) =>
    setGroups((g) =>
      g.map((grp, i) =>
        i === gIdx
          ? {
              ...grp,
              borrowers: grp.borrowers.map((b, j) =>
                j === bIdx ? { ...b, ...patch } : b,
              ),
            }
          : grp,
      ),
    );
  const removeBorrower = (gIdx, bIdx) =>
    setGroups((g) =>
      g.map((grp, i) =>
        i === gIdx
          ? { ...grp, borrowers: grp.borrowers.filter((_, j) => j !== bIdx) }
          : grp,
      ),
    );

  const handleSave = async () => {
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi];
      if (!g.branchId) {
        notify(`Branch ${gi + 1}: Please select a sub-client`, "error");
        return;
      }
      for (let bi = 0; bi < g.borrowers.length; bi++) {
        const b = g.borrowers[bi];
        if (!b.borrowerName?.trim()) {
          notify(
            `Branch ${gi + 1}, Borrower ${bi + 1}: Name is required`,
            "error",
          );
          return;
        }
        if (!b.subtotal || parseFloat(b.subtotal) <= 0) {
          notify(
            `Branch ${gi + 1}, Borrower ${bi + 1}: Sub total is required`,
            "error",
          );
          return;
        }
      }
    }
    if (!isBalanced) {
      notify("Sum of all borrowers must equal the bill sub total", "error");
      return;
    }

    const branchesPayload = groups.map((g) => ({
      branchId: g.branchId,
      branchName: g.branchName,
      borrowers: g.borrowers.map((b) => ({
        borrowerName: b.borrowerName,
        perAccount: parseFloat(b.perAccount) || 0,
        subtotal: parseFloat(b.subtotal),
      })),
    }));

    setSaving(true);
    try {
      await axios.post(
        `${BASE_URL}/size-wise-split/${invoiceId}/size-split-bills/${splitBillId}/branches`,
        { branches: branchesPayload },
      );
      notify("Size-wise split bill saved successfully!");
      setTimeout(() => navigate(-2), 1500);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to save branches",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!splitBillId) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 text-slate-400">
        <RiInboxLine className="w-12 h-12 text-indigo-200" />
        <p className="text-sm font-semibold">
          No split bill ID found. Please start from Step 1.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-xs text-indigo-500 hover:underline flex items-center gap-1"
        >
          <RiArrowLeftLine className="w-3.5 h-3.5" /> Go back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header */}
      <header className="bg-white border-b border-indigo-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl border border-indigo-100 flex items-center justify-center text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition"
            >
              <RiArrowLeftLine className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-md shadow-indigo-200">
              <FaCodeBranch className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-indigo-800 tracking-tight">
                Size-Wise Split Bill — Step 2
              </h1>
              <p className="text-[11px] text-indigo-400 tracking-widest uppercase">
                Add Branches & Borrowers ·{" "}
                <span className="font-bold text-violet-600">
                  {billNumber || invoiceNumber}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-4 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2">
              <div className="flex flex-col items-end">
                <span className="text-[10px] text-slate-400 uppercase tracking-widest">
                  Bill To
                </span>
                <span className="text-xs font-bold text-slate-700">
                  {clientName}
                </span>
              </div>
              <div className="w-px h-8 bg-indigo-200" />
              <div className="flex flex-col items-end">
                <span className="text-[10px] text-slate-400 uppercase tracking-widest">
                  Bill Sub Total
                </span>
                <span className="text-xs font-bold text-indigo-700 font-mono">
                  ₹{fmt(grandSubTotal)}
                </span>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !isBalanced || groups.length === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold shadow-md shadow-indigo-200 hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Spinner /> : <RiSaveLine className="w-4 h-4" />}
              {saving ? "Saving…" : "Save Branches"}
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 py-6 space-y-5 w-full">
        {/* Info cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-indigo-100 rounded-2xl px-5 py-4 shadow-sm flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
              <RiFileTextLine className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                Invoice
              </div>
              <div className="text-sm font-bold text-indigo-700 font-mono">
                {invoiceNumber}
              </div>
            </div>
          </div>
          <div className="bg-white border border-indigo-100 rounded-2xl px-5 py-4 shadow-sm flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
              <RiBuilding2Line className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                Client
              </div>
              <div className="text-sm font-bold text-slate-700 truncate max-w-[180px]">
                {clientName}
              </div>
            </div>
          </div>
          <div className="bg-white border border-indigo-100 rounded-2xl px-5 py-4 shadow-sm flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
              <LiaRupeeSignSolid className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                Bill Sub Total
              </div>
              <div className="text-sm font-bold text-emerald-700 font-mono">
                ₹{fmt(grandSubTotal)}
              </div>
            </div>
          </div>
        </div>

        <SummaryBar
          grandSubTotal={grandSubTotal}
          allocatedSubTotal={allocatedSubTotal}
          branchCount={groups.length}
          borrowerCount={totalBorrowers}
        />

        {/* Branch groups */}
        <div className="space-y-4">
          {groups.length === 0 ? (
            <div className="bg-white border border-indigo-100 rounded-2xl shadow-sm flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
              <RiInboxLine className="w-10 h-10 text-indigo-200" />
              <p className="text-sm font-semibold">No branches added yet.</p>
              <button
                onClick={addGroup}
                className="text-xs text-indigo-500 hover:underline flex items-center gap-1"
              >
                <RiAddLine className="w-3.5 h-3.5" /> Add a branch
              </button>
            </div>
          ) : (
            groups.map((group, gIdx) => (
              <BranchGroup
                key={group.id}
                group={group}
                groupIdx={gIdx}
                branches={branches}
                bankId={bankId}
                clientName={clientName}
                cgstRate={invCgst}
                sgstRate={invSgst}
                onUpdateGroup={updateGroup}
                onRemoveGroup={removeGroup}
                onAddBorrower={addBorrower}
                onUpdateBorrower={updateBorrower}
                onRemoveBorrower={removeBorrower}
                onBranchCreated={handleBranchCreated}
                notify={notify}
              />
            ))
          )}
        </div>

        <button
          onClick={addGroup}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-indigo-200 text-indigo-500 rounded-2xl text-sm font-semibold hover:border-indigo-400 hover:bg-indigo-50/50 transition"
        >
          {branchLoading ? <Spinner /> : <RiAddLine className="w-4 h-4" />}
          Add Branch Row
        </button>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-5 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-semibold hover:bg-slate-50 transition"
          >
            <RiArrowLeftLine className="w-4 h-4" /> Back to Step 1
          </button>
          <div className="flex items-center gap-3">
            {!isBalanced && groups.length > 0 && (
              <span className="text-xs text-amber-600 font-semibold">
                {allocatedSubTotal > grandSubTotal
                  ? `Over by ₹${fmt(allocatedSubTotal - grandSubTotal)}`
                  : `₹${fmt(grandSubTotal - allocatedSubTotal)} remaining to allocate`}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !isBalanced || groups.length === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold shadow-md shadow-indigo-200 hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Spinner /> : <RiSaveLine className="w-4 h-4" />}
              {saving ? "Saving…" : "Save Branches"}
            </button>
          </div>
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CreateSizeWiseSplitBill;
